/**
 * Quality Dashboard Component
 *
 * Shows agent quality metrics based on implicit RLHF signals.
 * Displays alignment, autonomy, safety, clarity scores with grades.
 * Surfaces actionable improvement suggestions.
 */

import { LitElement, html, css, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { raxApi } from "../../api/rax-api.js";
import { raxTheme } from "../styles/rax-theme.js";

export interface QualityMetrics {
  alignment: number; // 0-100
  autonomy: number; // 0-100
  safety: number; // 0-100
  clarity: number; // 0-100
  overall: number; // 0-100
  grade: string; // A+, A, B+, etc.
  signalCount: number;
  computedAt: string;
}

export interface ImprovementSuggestion {
  id: string;
  category: "alignment" | "autonomy" | "safety" | "clarity";
  title: string;
  description: string;
  action?: string;
}

@customElement("rax-quality-dashboard")
export class RaxQualityDashboard extends LitElement {
  static override styles = [
    raxTheme,
    css`
      :host {
        display: block;
      }
      
      .quality-dashboard {
        padding: var(--space-6);
        max-width: 800px;
        margin: 0 auto;
      }
      
      .dashboard-header {
        display: flex;
        align-items: center;
        gap: var(--space-3);
        margin-bottom: var(--space-6);
      }
      
      .dashboard-header h2 {
        font-size: var(--font-xl);
        font-weight: 700;
        color: var(--text-primary);
        margin: 0;
      }
      
      .dashboard-header .icon {
        font-size: 28px;
      }
      
      /* Overall Grade */
      .overall-grade {
        background: var(--bg-elevated);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-xl);
        padding: var(--space-6);
        text-align: center;
        margin-bottom: var(--space-6);
      }
      
      .grade-circle {
        width: 100px;
        height: 100px;
        border-radius: 50%;
        background: linear-gradient(135deg, var(--rax-yellow), var(--rax-amber));
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 0 auto var(--space-4);
        box-shadow: 0 0 30px rgba(255, 208, 0, 0.3);
      }
      
      .grade-letter {
        font-size: 42px;
        font-weight: 800;
        color: var(--rax-black);
      }
      
      .grade-label {
        font-size: var(--font-lg);
        font-weight: 600;
        color: var(--text-primary);
        margin-bottom: var(--space-1);
      }
      
      .grade-description {
        font-size: var(--font-sm);
        color: var(--text-secondary);
      }
      
      /* Metrics Grid */
      .metrics-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
        gap: var(--space-4);
        margin-bottom: var(--space-6);
      }
      
      .metric-card {
        background: var(--bg-elevated);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-lg);
        padding: var(--space-4);
      }
      
      .metric-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: var(--space-2);
      }
      
      .metric-name {
        font-size: var(--font-sm);
        font-weight: 600;
        color: var(--text-primary);
      }
      
      .metric-score {
        font-size: var(--font-lg);
        font-weight: 700;
        color: var(--rax-yellow);
      }
      
      .metric-bar {
        height: 8px;
        background: var(--bg-primary);
        border-radius: var(--radius-full);
        overflow: hidden;
        margin-bottom: var(--space-2);
      }
      
      .metric-fill {
        height: 100%;
        border-radius: var(--radius-full);
        transition: width 0.5s ease;
      }
      
      .metric-fill.high {
        background: linear-gradient(90deg, var(--success), #10b981);
      }
      
      .metric-fill.medium {
        background: linear-gradient(90deg, var(--rax-yellow), var(--rax-amber));
      }
      
      .metric-fill.low {
        background: linear-gradient(90deg, var(--warning), var(--error));
      }
      
      .metric-description {
        font-size: var(--font-xs);
        color: var(--text-secondary);
      }
      
      /* Suggestions */
      .suggestions-section {
        background: var(--bg-elevated);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-xl);
        padding: var(--space-5);
      }
      
      .suggestions-header {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        margin-bottom: var(--space-4);
      }
      
      .suggestions-header h3 {
        font-size: var(--font-md);
        font-weight: 600;
        color: var(--text-primary);
        margin: 0;
      }
      
      .suggestion-list {
        display: flex;
        flex-direction: column;
        gap: var(--space-3);
      }
      
      .suggestion-item {
        display: flex;
        gap: var(--space-3);
        padding: var(--space-3);
        background: var(--bg-primary);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-md);
      }
      
      .suggestion-number {
        width: 24px;
        height: 24px;
        background: var(--rax-yellow);
        color: var(--rax-black);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: var(--font-xs);
        font-weight: 700;
        flex-shrink: 0;
      }
      
      .suggestion-content {
        flex: 1;
      }
      
      .suggestion-title {
        font-size: var(--font-sm);
        font-weight: 500;
        color: var(--text-primary);
        margin-bottom: var(--space-1);
      }
      
      .suggestion-desc {
        font-size: var(--font-xs);
        color: var(--text-secondary);
      }
      
      .suggestion-action {
        margin-top: var(--space-2);
      }
      
      .suggestion-action button {
        font-size: var(--font-xs);
        padding: var(--space-1) var(--space-3);
        background: transparent;
        border: 1px solid var(--rax-yellow);
        color: var(--rax-yellow);
        border-radius: var(--radius-sm);
        cursor: pointer;
        transition: all var(--transition-fast);
      }
      
      .suggestion-action button:hover {
        background: var(--rax-yellow);
        color: var(--rax-black);
      }
      
      /* Empty State */
      .empty-state {
        text-align: center;
        padding: var(--space-8);
        color: var(--text-secondary);
      }
      
      .empty-icon {
        font-size: 48px;
        margin-bottom: var(--space-4);
      }
      
      /* Loading */
      .loading {
        text-align: center;
        padding: var(--space-8);
        color: var(--text-secondary);
      }
      
      /* Signal count footer */
      .signal-count {
        text-align: center;
        font-size: var(--font-xs);
        color: var(--text-tertiary);
        margin-top: var(--space-4);
      }
    `,
  ];

  @property({ type: String })
  agentId: string | null = null;

  @state()
  private loading = true;

  @state()
  private metrics: QualityMetrics | null = null;

  @state()
  private suggestions: ImprovementSuggestion[] = [];

  @state()
  private error: string | null = null;

  override async connectedCallback() {
    super.connectedCallback();
    await this.loadData();
  }

  private async loadData() {
    this.loading = true;
    this.error = null;

    try {
      const res = await raxApi.request<{
        metrics: QualityMetrics;
        suggestions: ImprovementSuggestion[];
      }>("GET", "/api/rax/quality");

      if (res.success && res.data) {
        this.metrics = res.data.metrics;
        this.suggestions = res.data.suggestions || [];
      } else {
        // Show placeholder if no data yet
        this.metrics = null;
      }
    } catch (err) {
      console.error("[QualityDashboard] Load error:", err);
      this.error = "Failed to load quality metrics";
    } finally {
      this.loading = false;
    }
  }

  override render() {
    if (this.loading) {
      return html`
        <div class="quality-dashboard">
          <div class="loading">Loading quality metrics...</div>
        </div>
      `;
    }

    if (this.error) {
      return html`
        <div class="quality-dashboard">
          <div class="empty-state">
            <div class="empty-icon">⚠️</div>
            <div>${this.error}</div>
          </div>
        </div>
      `;
    }

    if (!this.metrics) {
      return html`
        <div class="quality-dashboard">
          <div class="dashboard-header">
            <span class="icon">📊</span>
            <h2>Agent Quality</h2>
          </div>
          <div class="empty-state">
            <div class="empty-icon">📊</div>
            <div>Not enough data yet</div>
            <p style="margin-top: var(--space-2)">
              Keep chatting with your agent. Quality metrics will appear after more interactions.
            </p>
          </div>
        </div>
      `;
    }

    return html`
      <div class="quality-dashboard">
        <div class="dashboard-header">
          <span class="icon">📊</span>
          <h2>Agent Quality</h2>
        </div>

        ${this.renderOverallGrade()}
        ${this.renderMetricsGrid()}
        ${this.suggestions.length > 0 ? this.renderSuggestions() : nothing}

        <div class="signal-count">
          Based on ${this.metrics.signalCount} interaction signals
        </div>
      </div>
    `;
  }

  private renderOverallGrade() {
    if (!this.metrics) return nothing;

    const gradeDescriptions: Record<string, string> = {
      "A+": "Outstanding - Your agent is performing excellently",
      A: "Excellent - Your agent gets you almost all the time",
      "A-": "Very Good - Your agent is doing great",
      "B+": "Good - Your agent is solid with room to improve",
      B: "Above Average - Decent performance overall",
      "B-": "Satisfactory - Some areas need attention",
      "C+": "Fair - Noticeable issues to address",
      C: "Needs Work - Several areas need improvement",
      "C-": "Below Average - Significant improvements needed",
      D: "Poor - Major issues affecting usefulness",
      F: "Failing - Requires immediate attention",
    };

    return html`
      <div class="overall-grade">
        <div class="grade-circle">
          <span class="grade-letter">${this.metrics.grade}</span>
        </div>
        <div class="grade-label">Overall Quality</div>
        <div class="grade-description">
          ${gradeDescriptions[this.metrics.grade] || "Quality assessment based on your interactions"}
        </div>
      </div>
    `;
  }

  private renderMetricsGrid() {
    if (!this.metrics) return nothing;

    const dimensions = [
      {
        key: "alignment",
        name: "Alignment",
        score: this.metrics.alignment,
        description: '"Gets you"',
      },
      {
        key: "autonomy",
        name: "Autonomy",
        score: this.metrics.autonomy,
        description: '"Self-sufficient"',
      },
      {
        key: "safety",
        name: "Safety",
        score: this.metrics.safety,
        description: '"Careful"',
      },
      {
        key: "clarity",
        name: "Clarity",
        score: this.metrics.clarity,
        description: '"Clear communicator"',
      },
    ];

    return html`
      <div class="metrics-grid">
        ${dimensions.map(
          (d) => html`
          <div class="metric-card">
            <div class="metric-header">
              <span class="metric-name">${d.name}</span>
              <span class="metric-score">${d.score}%</span>
            </div>
            <div class="metric-bar">
              <div
                class="metric-fill ${this.getScoreClass(d.score)}"
                style="width: ${d.score}%"
              ></div>
            </div>
            <div class="metric-description">${d.description}</div>
          </div>
        `,
        )}
      </div>
    `;
  }

  private renderSuggestions() {
    return html`
      <div class="suggestions-section">
        <div class="suggestions-header">
          <span>💡</span>
          <h3>Suggested Improvements</h3>
        </div>
        <div class="suggestion-list">
          ${this.suggestions.slice(0, 3).map(
            (s, i) => html`
            <div class="suggestion-item">
              <span class="suggestion-number">${i + 1}</span>
              <div class="suggestion-content">
                <div class="suggestion-title">${s.title}</div>
                <div class="suggestion-desc">${s.description}</div>
                ${
                  s.action
                    ? html`
                  <div class="suggestion-action">
                    <button @click=${() => this.onApplySuggestion(s)}>
                      ${s.action}
                    </button>
                  </div>
                `
                    : nothing
                }
              </div>
            </div>
          `,
          )}
        </div>
      </div>
    `;
  }

  private getScoreClass(score: number): string {
    if (score >= 80) return "high";
    if (score >= 50) return "medium";
    return "low";
  }

  private onApplySuggestion(suggestion: ImprovementSuggestion) {
    this.dispatchEvent(
      new CustomEvent("apply-suggestion", {
        bubbles: true,
        detail: { suggestion },
      }),
    );
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "rax-quality-dashboard": RaxQualityDashboard;
  }
}
